import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ROUTES_PATH } from "../../utils/constants";
import { BankreaderService } from "../../services/Bankreader";
import { IRootState } from "../../redux/store";
import { sendError } from "../../redux/errorSlice";
import { ErrorType } from "../../models/ErrorType";
import { isLoading } from "../../redux/loaderSlice";
import { selectFlowStep } from "../../redux/headerSlice";
import { WebsocketCode } from "../../models/Websocket";
import { credentialsValidated, resettingLogin } from "../../redux/loginSlice";
import { clearBank } from "../../redux/providerSlice";
import { EventMessageService } from "../../services/EventMessage";
import InsightService from "../../services/Insight";
import { StorageService } from "../../services/Storage";
import { saveRequest } from "../../redux/requestSlice";
import { AggregationErrorType, AggregationType } from "../../models/AggregationType";

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bankSelected = useSelector((state: IRootState) => state.provider.value.bank_selected?.name);
  const bankPreselected = useSelector((state: IRootState) => state.request.value.data.third_party_bank);
  const bankreaderId = useSelector((state: IRootState) => state.request.value.data.bankreader_id);
  const thirdPartyUrl = useSelector((state: IRootState) => state.request.value.data.third_party_link);
  const defaultBank = useSelector((state: IRootState) => state.request.value.isDefaultBank);
  const pepperOne = useSelector((state: IRootState) => state.params.value.pepperOne);
  const reset = useSelector((state: IRootState) => state.login.value.resetting);
  const userEntered = useSelector((state: IRootState) => state.login.value.credentials);

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [aggregationExecuted, setAggregationExecuted] = useState<boolean>(false);
  const [authData, saveAuthData] = useState<any>('');
  const [validCallback, setValidCallback] = useState<boolean>(false);

  const loginReturn = () => {
    if (userEntered) {
      dispatch(isLoading(true));
      dispatch(resettingLogin(true))
    } else {
      if (process.env.REACT_APP_ENV === 'development') {
        navigate(ROUTES_PATH.ENTRY);
      } else {
        dispatch(clearBank());
        navigate(ROUTES_PATH.ENTITIES);
      }
    }
  }

  const aggregationErrors = async (bankreaderId: string, aggregationError: AggregationErrorType) => {
    try {
      await BankreaderService.handleAggregationError(bankreaderId, aggregationError)
    } catch (error) {
      console.log(error);
    }
  }

  const receiveMessage = async (event: MessageEvent) => {
    if (event.data.type === 'PEPPER_ONE' && event.data.message === 'return') {
      loginReturn();
    } else if (event.origin === 'https://link.tink.com' && !validCallback) {
      setValidCallback(true)
    } else if (event.origin !== 'https://link.tink.com') {
      return;
    }

    const { type, data, error, additionalInformation } = JSON.parse(event.data);
    if (type === 'status') {
      const { loading } = data;
      if (loading) {
        dispatch(isLoading(true));
      } else {
        !iframeUrl && dispatch(isLoading(false));
      }
    } else {
      InsightService.logTrace("TinkEvent", [
        { bankreaderId: bankreaderId},
        { type: type },
        { data: data },
        { additionalInformation: additionalInformation }
      ]);
      if (type === 'code') {
        pepperOne && EventMessageService.sendMessageEvent('hide-return-button')
        const code = data;
        saveAuthData({
          code: code,
          credentialsId: additionalInformation.credentialsId
        });
      } else if (type === 'credentials') {
        // Handle credentials error response from Tink Link
        const credentialsId = data;
        console.log(`Authentication failed with credentials identifier: ${credentialsId} with error status: ${error.status} and error message: ${error.message}.`);
        const aggregationError: AggregationErrorType = {
          third_party: 'TINK',
          tracking_id: error.trackingId,
          error: error.status,
          error_reason: error.reason,
          error_type: error.type,
          message: error.message,
          credentials_id: credentialsId,
          provider_name: bankSelected ?? bankPreselected,
        }
        if (aggregationError.error && aggregationError.error_type && aggregationError.message) {
          await aggregationErrors(bankreaderId, aggregationError);
        }
      } else if (type === 'error') {
        const aggregationError: AggregationErrorType = {
          third_party: 'TINK',
          tracking_id: error.trackingId,
          error: error.status,
          error_reason: error.reason,
          error_type: 'LOGIN',
          message: error.message,
          credentials_id: error.credentialsId,
          provider_name: bankSelected ?? bankPreselected,
        }
  
        if (aggregationError.error && aggregationError.error_type && aggregationError.message) {
          await aggregationErrors(bankreaderId, aggregationError);
        }

        if (error.status === 'USER_CANCELLED') {
          if (process.env.REACT_APP_ENV === 'development') {
            navigate(ROUTES_PATH.ENTRY);
          } else {
            navigate(ROUTES_PATH.ENTITIES);
          }
        } else {
          BankreaderService.sendWebsocketCode(bankreaderId, { code: WebsocketCode.LOGIN_FAILED });
          dispatch(sendError(ErrorType.BANK_FAILED));
          navigate(ROUTES_PATH.UNKNOWN_ERROR);
        }
      } else if (type === 'application-event') {
        const { event } = data;
        if (event === 'INITIALIZED') {
          pepperOne && EventMessageService.sendMessageEvent('return-button');
          dispatch(isLoading(false));
        } else if (event === 'PROVIDER_AUTHENTICATION_INITIALIZED') {
          dispatch(isLoading(true));
          dispatch(credentialsValidated(true));
        } else if (event === 'CREDENTIALS_VALIDATION_FAILED') {
          const aggregationError: AggregationErrorType = {
            third_party: 'TINK',
            tracking_id: null,
            error: 'CREDENTIALS_VALIDATION_FAILED',
            error_type: 'LOGIN',
            error_reason: null,
            message: 'Error de autenticación',
            provider_name: bankSelected ?? bankPreselected,
          }
    
          if (aggregationError.error && aggregationError.error_type && aggregationError.message) {
            await aggregationErrors(bankreaderId, aggregationError);
          }

          BankreaderService.sendWebsocketCode(bankreaderId, { code: WebsocketCode.LOGIN_FAILED });
          navigate(ROUTES_PATH.LOGIN_ERROR);
        } else if (event === 'AUTHENTICATION_SUCCESSFUL') {
          // BankreaderService.sendWebsocketCode(bankreaderId, { code: WebsocketCode.CLIENT_DATA });
        }
      } else if (type === 'view-event') {
        if (data.screen === 'SUCCESS_SCREEN') {
          dispatch(isLoading(true));
          BankreaderService.sendWebsocketCode(bankreaderId, { code: WebsocketCode.CLIENT_DATA });
        } else {
          dispatch(isLoading(false));
        }
      } else {
        // More message types may be added in the future
      }
    }
  }

  const handleAggregation = async (auth: { code: string, credentialsId: string }) => {
    const provider = bankSelected || bankPreselected;
    const aggregation: AggregationType = {
      code: auth.code,
      credentials_id: auth.credentialsId,
      third_party: 'TINK',
      provider_name: provider
    }
    BankreaderService.handleAggregation(bankreaderId, aggregation)
      .then(() => {
        EventMessageService.sendMessageEvent('flowStepSuccess');
        navigate(ROUTES_PATH.SUCCESS);
      })
      .catch((error: any) => {
        dispatch(sendError(ErrorType.AGGREGATION_FAILED));
        navigate(ROUTES_PATH.UNKNOWN_ERROR);
      })
  }

  const addQueryParams = (thirdPartyUrl: string) => {
    let urlString;
    const host = new URL(window.location.href);
    let newUrl = new URL(thirdPartyUrl);
    let searchParams = newUrl.searchParams;
    searchParams.set('redirect_uri', host.origin);
    newUrl.search = searchParams.toString();
    urlString = decodeURIComponent(newUrl.toString());
    console.log('URL iFrame:', urlString);

    if (bankSelected) {
      let updatedUrl;
      if (defaultBank) {
        updatedUrl = `${urlString}&locale=es_ES&iframe=true`
      } else {
        let newUrl = new URL(urlString);
        let searchParams = newUrl.searchParams;
        searchParams.set('input_provider', bankSelected);
        newUrl.search = searchParams.toString();
        let url = decodeURIComponent(newUrl.toString());
        updatedUrl = `${url}&input_provider=${bankSelected}`
      }
      const providerUrl = `${updatedUrl}&locale=es_ES&iframe=true`
      setIframeUrl(providerUrl);
    } else if (process.env.REACT_APP_ENV === 'development') {
      const url = `${urlString}&locale=es_ES&iframe=true`
      setIframeUrl(url);
    } else {
      navigate(ROUTES_PATH.ENTITIES);
    }
  }

  const launchiFrame = () => {
    window.addEventListener('message', receiveMessage);
    const storedData = StorageService.getBankreaderStore();
    if (thirdPartyUrl) {
      addQueryParams(thirdPartyUrl);
    } else if (storedData && storedData.third_party_link) {
      dispatch(saveRequest(StorageService.getBankreaderStore()));
      addQueryParams(storedData.third_party_link);
    } else {
      dispatch(sendError(ErrorType.PLATFORM_FAILED));
      navigate(ROUTES_PATH.UNKNOWN_ERROR);
    }

  }

  useEffect(() => {
    return () => {
      window.removeEventListener('message', receiveMessage);
    }
  }, [])
  

  useEffect(() => {
    if (authData && !aggregationExecuted) {
      setAggregationExecuted(true);
      handleAggregation(authData);
    }
  }, [authData])


  useEffect(() => {
    if (reset) {
      setIframeUrl(null);
      dispatch(resettingLogin(false));
      window.removeEventListener('message', receiveMessage);
    }
  }, [reset])

  useEffect(() => {
    if (userEntered) {
      dispatch(selectFlowStep(3));
    } else {
      dispatch(selectFlowStep(2));
    }
  }, [userEntered])


  useEffect(() => {
    dispatch(selectFlowStep(2));
    if (iframeUrl) {
      dispatch(credentialsValidated(false));
    } else {
      window.removeEventListener('message', receiveMessage);
      launchiFrame();
    }
  }, [iframeUrl]);


  return (
    <div className="login-container pepper-full-height">
      {iframeUrl && <iframe title='tink-iframe' src={iframeUrl} sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-popups-to-escape-sandbox allow-top-navigation" />}
    </div>
  )
}
